// School V2 Mock Data - School View with Sets
// This is for School V2 View (SchoolV2TimetableGrid)
// School: Hollins - Year Groups: 7, 8, 9, 10, 11
// Each Year Group has 8 sets: A, B, C, D, E, F, G, H
// Cell shows: Subject, TeacherAbbr, Room (e.g., "Art, GSW, R101")
// Popup shows: Subject (title), Teacher full name, Room (and capacity), Date, Time

export interface SchoolV2CellData {
  id: string;
  label: string; // "Subject, TeacherAbbr, Room" format
  color: string;
  isUnavailable?: boolean;
  popupData: {
    title: string;
    subtitle?: string;
    details: { label: string; value: string }[];
  };
}

export interface PeriodInfo {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  type: 'lesson' | 'registration' | 'break' | 'lunch';
}

export interface SetData {
  id: string; // e.g., "7A", "7B"
  name: string; // e.g., "7A"
  // Cells indexed by period-week-day (e.g., "p1-weekA-monday")
  cells: Record<string, SchoolV2CellData | null>;
}

export interface YearGroupV2Data {
  id: string; // e.g., "year-7"
  name: string; // e.g., "Year 7"
  color: string;
  periods: PeriodInfo[];
  sets: SetData[]; // 8 sets: A, B, C, D, E, F, G, H
}

export interface SchoolV2Data {
  yearGroups: YearGroupV2Data[];
  weeks: ('A' | 'B')[];
  days: string[];
  sets: string[]; // ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
}

// Subject colors - each subject has a unique color
const subjectColors: Record<string, string> = {
  Eng: '#10B981', // Green
  Mat: '#3B82F6', // Blue
  Sci: '#8B5CF6', // Purple
  DT: '#F59E0B', // Amber
  His: '#EF4444', // Red
  MFL: '#0EA5E9', // Sky
  Geo: '#EC4899', // Pink
  Art: '#6366F1', // Indigo
  PE: '#FDBA74', // Orange
  Mus: '#A5B4FC', // Light Indigo
  RE: '#BEF264', // Lime
  Dra: '#F472B6', // Pink
};

// Full subject names
const subjectFullNames: Record<string, string> = {
  Eng: 'English',
  Mat: 'Mathematics',
  Sci: 'Science',
  DT: 'Design Technology',
  His: 'History',
  MFL: 'Modern Foreign Languages',
  Geo: 'Geography',
  Art: 'Art',
  PE: 'Physical Education',
  Mus: 'Music',
  RE: 'Religious Education',
  Dra: 'Drama',
};

// Year group colors (5 distinct colors)
const yearGroupColors: Record<number, string> = {
  7: '#DBEAFE', // Light blue
  8: '#D1FAE5', // Light green
  9: '#FEE2E2', // Light red
  10: '#F3E8FF', // Light purple
  11: '#FEF3C7', // Light yellow
};

// Teachers with abbreviations and full names
interface TeacherInfo {
  abbr: string; // 3-letter abbreviation
  fullName: string;
  subject: string;
}

const allTeachers: TeacherInfo[] = [
  // English Teachers
  { abbr: 'SHW', fullName: 'Mr S Howard', subject: 'Eng' },
  { abbr: 'NTA', fullName: 'Ms N Taylor', subject: 'Eng' },
  { abbr: 'SWA', fullName: 'Mrs S Walsh', subject: 'Eng' },
  { abbr: 'JBE', fullName: 'Mr J Bennett', subject: 'Eng' },
  // Maths Teachers
  { abbr: 'JGA', fullName: 'Mr J Garcia', subject: 'Mat' },
  { abbr: 'NBR', fullName: 'Mrs N Brown', subject: 'Mat' },
  { abbr: 'DHA', fullName: 'Mr D Hall', subject: 'Mat' },
  { abbr: 'KWI', fullName: 'Ms K Williams', subject: 'Mat' },
  // Science Teachers
  { abbr: 'ECA', fullName: 'Dr E Carter', subject: 'Sci' },
  { abbr: 'SBM', fullName: 'Mr S Bloom', subject: 'Sci' },
  { abbr: 'RPA', fullName: 'Mrs R Parker', subject: 'Sci' },
  { abbr: 'MFO', fullName: 'Dr M Foster', subject: 'Sci' },
  // Design Teachers
  { abbr: 'SSC', fullName: 'Mr S Scott', subject: 'DT' },
  { abbr: 'EBA', fullName: 'Ms E Baker', subject: 'DT' },
  { abbr: 'TCO', fullName: 'Mr T Cooper', subject: 'DT' },
  // History Teachers
  { abbr: 'CWA', fullName: 'Mr C Watson', subject: 'His' },
  { abbr: 'ASA', fullName: 'Ms A Sanders', subject: 'His' },
  { abbr: 'JMO', fullName: 'Mrs J Morgan', subject: 'His' },
  // MFL Teachers
  { abbr: 'CKI', fullName: 'Mrs C King', subject: 'MFL' },
  { abbr: 'JGR', fullName: 'Mr J Green', subject: 'MFL' },
  { abbr: 'HDO', fullName: 'Ms H Donovan', subject: 'MFL' },
  { abbr: 'PMA', fullName: 'Mr P Martinez', subject: 'MFL' },
  // Geography Teachers
  { abbr: 'TDU', fullName: 'Mr T Duncan', subject: 'Geo' },
  { abbr: 'NJO', fullName: 'Mrs N Jones', subject: 'Geo' },
  { abbr: 'HUM', fullName: 'Dr H Humphrey', subject: 'Geo' },
  // Art Teachers
  { abbr: 'LKE', fullName: 'Ms L Kelly', subject: 'Art' },
  { abbr: 'PDR', fullName: 'Mr P Drew', subject: 'Art' },
  { abbr: 'LWH', fullName: 'Mrs L White', subject: 'Art' },
  // PE Teachers
  { abbr: 'GSW', fullName: 'Mr G Swift', subject: 'PE' },
  { abbr: 'AHU', fullName: 'Ms A Hunter', subject: 'PE' },
  { abbr: 'BRA', fullName: 'Mr B Ramos', subject: 'PE' },
  // Music Teachers
  { abbr: 'MCL', fullName: 'Mrs M Clark', subject: 'Mus' },
  { abbr: 'DRO', fullName: 'Mr D Roberts', subject: 'Mus' },
  // RE Teachers
  { abbr: 'SLE', fullName: 'Ms S Lewis', subject: 'RE' },
  { abbr: 'JTH', fullName: 'Mr J Thomas', subject: 'RE' },
  // Drama Teachers
  { abbr: 'EMI', fullName: 'Ms E Miller', subject: 'Dra' },
  { abbr: 'RDA', fullName: 'Mr R Davis', subject: 'Dra' },
];

// All rooms with capacities
interface RoomInfo {
  name: string;
  capacity: number;
}

const allRooms: RoomInfo[] = [
  { name: 'GYM', capacity: 120 },
  { name: 'Hall', capacity: 200 },
  { name: 'LRC', capacity: 60 },
  { name: 'Art1', capacity: 28 },
  { name: 'Art2', capacity: 28 },
  { name: 'Mus1', capacity: 30 },
  { name: 'Mus2', capacity: 25 },
  { name: 'DT1', capacity: 24 },
  { name: 'DT2', capacity: 24 },
  { name: 'Sci1', capacity: 30 },
  { name: 'Sci2', capacity: 30 },
  { name: 'Sci3', capacity: 28 },
  ...Array.from({ length: 20 }, (_, i) => ({
    name: `R${i + 101}`,
    capacity: 25 + Math.floor((i % 4) * 5),
  })),
];

// Subjects array
const subjects = Object.keys(subjectColors);

// Year groups: 7, 8 (removed 9, 10, 11)
const yearGroups = [7, 8];

// Set letters
const setLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

// Dates for January 2026
const weekDates: Record<string, Record<string, string>> = {
  A: {
    Monday: 'Mon 5th Jan 2026',
    Tuesday: 'Tue 6th Jan 2026',
    Wednesday: 'Wed 7th Jan 2026',
    Thursday: 'Thu 8th Jan 2026',
    Friday: 'Fri 9th Jan 2026',
  },
  B: {
    Monday: 'Mon 12th Jan 2026',
    Tuesday: 'Tue 13th Jan 2026',
    Wednesday: 'Wed 14th Jan 2026',
    Thursday: 'Thu 15th Jan 2026',
    Friday: 'Fri 16th Jan 2026',
  },
};

// Seeded random for consistency
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const standardPeriods: PeriodInfo[] = [
  { id: 'reg', name: 'Registration', startTime: '08.45', endTime: '09.00', type: 'registration' },
  { id: 'p1', name: '1', startTime: '09.05', endTime: '10.05', type: 'lesson' },
  { id: 'p2', name: '2', startTime: '10.05', endTime: '11.05', type: 'lesson' },
  { id: 'brk', name: 'Break', startTime: '11.05', endTime: '11.20', type: 'break' },
  { id: 'p3', name: '3', startTime: '11.20', endTime: '12.20', type: 'lesson' },
  { id: 'lun', name: 'Lunch', startTime: '12.20', endTime: '13.00', type: 'lunch' },
  { id: 'p4', name: '4', startTime: '13.00', endTime: '14.00', type: 'lesson' },
  { id: 'p5', name: '5', startTime: '14.00', endTime: '15.00', type: 'lesson' },
];

// Create cell for School V2 View
// Cell shows: Subject, TeacherAbbr, Room (e.g., "Art, GSW, R101")
// Popup shows: Subject (title), Teacher full name, Room (and capacity), Date, Time
const createSchoolV2Cell = (
  id: string,
  subject: string,
  yearGroup: number,
  setLetter: string,
  room: RoomInfo,
  teacher: TeacherInfo,
  week: 'A' | 'B',
  day: string,
  period: PeriodInfo,
  isUnavailable = false,
): SchoolV2CellData => {
  const subjectCode = subject;
  const subjectName = subjectFullNames[subject] || subject;
  const color = subjectColors[subject] || '#94A3B8';
  const date = weekDates[week][day];

  // Cell label for School V2 View: Subject, TeacherAbbr, Room
  const label = `${subjectCode}, ${teacher.abbr}, ${room.name}`;

  return {
    id,
    label,
    color,
    isUnavailable,
    popupData: {
      title: `${subjectName} - ${yearGroup}${setLetter}`,
      subtitle: subjectCode,
      details: [
        { label: 'Teacher', value: teacher.fullName },
        { label: 'Room', value: `${room.name} (Capacity: ${room.capacity})` },
        { label: 'Date', value: date },
        { label: 'Time', value: `${period.startTime} - ${period.endTime}` },
      ],
    },
  };
};

const generateYearGroups = (): YearGroupV2Data[] => {
  const weeks: ('A' | 'B')[] = ['A', 'B'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  return yearGroups.map((yearNum, yearIndex) => {
    // Generate sets for this year group
    const sets: SetData[] = setLetters.map((setLetter, setIndex) => {
      const cells: Record<string, SchoolV2CellData | null> = {};

      standardPeriods.forEach((period, periodIndex) => {
        weeks.forEach((week, weekIndex) => {
          days.forEach((day, dayIndex) => {
            const key = `${period.id}-week${week}-${day.toLowerCase()}`;

            // Non-lesson periods don't have cells
            if (period.type !== 'lesson') {
              cells[key] = null;
              return;
            }

            const seed = yearIndex * 100000 + setIndex * 10000 + weekIndex * 1000 + dayIndex * 100 + periodIndex;

            // Randomly determine if this cell has a lesson (90% chance)
            if (seededRandom(seed) > 0.1) {
              const subjectIndex = Math.floor(seededRandom(seed + 1) * subjects.length);
              const subject = subjects[subjectIndex];
              const room = allRooms[Math.floor(seededRandom(seed + 2) * allRooms.length)];

              // Get a teacher for this subject
              const subjectTeachers = allTeachers.filter((t) => t.subject === subject);
              const teacher =
                subjectTeachers.length > 0
                  ? subjectTeachers[Math.floor(seededRandom(seed + 3) * subjectTeachers.length)]
                  : allTeachers[Math.floor(seededRandom(seed + 3) * allTeachers.length)];

              cells[key] = createSchoolV2Cell(
                `year${yearNum}-set${setLetter}-${key}`,
                subject,
                yearNum,
                setLetter,
                room,
                teacher,
                week,
                day,
                period,
                // Mark some as unavailable (about 2%)
                seededRandom(seed + 4) < 0.02,
              );
            } else {
              cells[key] = null;
            }
          });
        });
      });

      return {
        id: `${yearNum}${setLetter}`,
        name: `${yearNum}${setLetter}`,
        cells,
      };
    });

    return {
      id: `year-${yearNum}`,
      name: `Year ${yearNum}`,
      color: yearGroupColors[yearNum],
      periods: standardPeriods,
      sets,
    };
  });
};

export const schoolV2MockData: SchoolV2Data = {
  yearGroups: generateYearGroups(),
  weeks: ['A', 'B'],
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  sets: setLetters,
};

export default schoolV2MockData;
